import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, roleConfig } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  User, 
  Mail, 
  Phone, 
  Building2, 
  Briefcase, 
  IdCard,
  Loader2,
  Save,
  Camera,
  Send,
  CheckCircle2,
  Unplug
} from 'lucide-react';
import { toast } from 'sonner';
import { UserAvatar } from '../components/UserAvatar';

export default function ProfilePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [profile, setProfile] = useState(null);
  const [gerencias, setGerencias] = useState([]);
  const [telegram, setTelegram] = useState({ configured: false, linked: false, deeplink: '' });
  const [tgLoading, setTgLoading] = useState(false);
  
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    rut: '',
    cargo: '',
    gerencia_area: '',
    telefono: ''
  });

  useEffect(() => {
    fetchProfile();
    fetchGerencias();
    fetchTelegram();
  }, []);

  const fetchTelegram = async () => {
    try {
      const r = await axios.get(`${API_URL}/telegram/status`);
      setTelegram(r.data);
    } catch (e) {
      // silent
    }
  };

  const connectTelegram = () => {
    if (!telegram.deeplink) return;
    window.open(telegram.deeplink, '_blank', 'noopener');
    toast.info('Presiona "Iniciar" en Telegram. Volveremos a verificar el estado.');
  };

  const refreshTelegramStatus = async () => {
    setTgLoading(true);
    await fetchTelegram();
    setTgLoading(false);
  };

  const disconnectTelegram = async () => {
    setTgLoading(true);
    try {
      await axios.post(`${API_URL}/telegram/disconnect`);
      toast.success('Telegram desvinculado');
      await fetchTelegram();
    } catch (e) {
      toast.error('No se pudo desvincular');
    } finally {
      setTgLoading(false);
    }
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setProfile(response.data);
      setForm({
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        rut: response.data.rut || '',
        cargo: response.data.cargo || '',
        gerencia_area: response.data.gerencia_area || '',
        telefono: response.data.telefono || ''
      });
    } catch (error) {
      toast.error('Error al cargar perfil');
    } finally {
      setLoading(false);
    }
  };

  const fetchGerencias = async () => {
    try {
      const response = await axios.get(`${API_URL}/gerencias`);
      setGerencias(response.data);
    } catch (error) {
      console.error('Error al cargar gerencias:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.patch(`${API_URL}/auth/profile`, form);
      toast.success('Perfil actualizado exitosamente');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  // Función para formatear RUT chileno
  const formatRut = (value) => {
    let rut = value.replace(/[^0-9kK]/g, '');
    
    if (rut.length > 1) {
      const dv = rut.slice(-1);
      let cuerpo = rut.slice(0, -1);
      
      let formatted = '';
      while (cuerpo.length > 3) {
        formatted = '.' + cuerpo.slice(-3) + formatted;
        cuerpo = cuerpo.slice(0, -3);
      }
      formatted = cuerpo + formatted + '-' + dv.toUpperCase();
      return formatted;
    }
    return rut;
  };

  const handleRutChange = (e) => {
    const formatted = formatRut(e.target.value);
    setForm({ ...form, rut: formatted });
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/auth/avatar`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Foto de perfil actualizada');
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir imagen');
    } finally { setUploadingAvatar(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#7FD02E]" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-gray-800">Mi Perfil</h1>
        <p className="text-gray-500">Gestiona tu información personal y laboral</p>
      </div>

      {/* Profile Card */}
      <Card className="border-gray-200 bg-white shadow-sm" data-testid="profile-info-card">
        <CardHeader className="border-b border-gray-100">
          <div className="flex items-center gap-4">
            <div className="relative group">
              <UserAvatar user={profile} size="xl" />
              <label className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center" data-testid="avatar-upload-label">
                {uploadingAvatar ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Camera className="w-5 h-5 text-white" />}
                <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} disabled={uploadingAvatar} data-testid="avatar-file-input" />
              </label>
            </div>
            <div>
              <CardTitle className="font-heading text-xl text-gray-800">
                {profile?.first_name} {profile?.last_name}
              </CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={roleConfig[profile?.role]?.color}>
                  {roleConfig[profile?.role]?.label}
                </Badge>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-gray-600">
            <Mail className="w-4 h-4" />
            <span>{profile?.email}</span>
          </div>
        </CardContent>
      </Card>

      {/* Edit Form */}
      <Card className="border-gray-200 bg-white shadow-sm" data-testid="profile-edit-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-gray-800 flex items-center gap-2">
            <IdCard className="w-5 h-5 text-[#7FD02E]" />
            Información Laboral
          </CardTitle>
          <CardDescription className="text-gray-500">
            Actualiza tu información personal y de contacto
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Datos Personales */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-gray-700 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Nombre
                </Label>
                <Input
                  id="first_name"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  placeholder="Tu nombre"
                  className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                  data-testid="profile-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-gray-700 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Apellido
                </Label>
                <Input
                  id="last_name"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  placeholder="Tu apellido"
                  className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                  data-testid="profile-lastname-input"
                />
              </div>
            </div>

            {/* RUT */}
            <div className="space-y-2">
              <Label htmlFor="rut" className="text-gray-700 flex items-center gap-2">
                <IdCard className="w-4 h-4" />
                RUT
              </Label>
              <Input
                id="rut"
                value={form.rut}
                onChange={handleRutChange}
                placeholder="12.345.678-9"
                maxLength={12}
                className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                data-testid="profile-rut-input"
              />
            </div>

            {/* Cargo y Área */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cargo" className="text-gray-700 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Cargo
                </Label>
                <Input
                  id="cargo"
                  value={form.cargo}
                  onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                  placeholder="Ej: Analista de Sistemas"
                  className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                  data-testid="profile-cargo-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Gerencia / Área
                </Label>
                {gerencias.length > 0 ? (
                  <Select
                    value={form.gerencia_area}
                    onValueChange={(value) => setForm({ ...form, gerencia_area: value })}
                  >
                    <SelectTrigger 
                      className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                      data-testid="profile-area-select"
                    >
                      <SelectValue placeholder="Seleccionar gerencia/área" />
                    </SelectTrigger>
                    <SelectContent>
                      {gerencias.map((gerencia) => (
                        <SelectItem key={gerencia.id} value={gerencia.name}>
                          {gerencia.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={form.gerencia_area}
                    onChange={(e) => setForm({ ...form, gerencia_area: e.target.value })}
                    placeholder="Ej: Gerencia de TI"
                    className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                    data-testid="profile-area-input"
                  />
                )}
              </div>
            </div>

            {/* Teléfono */}
            <div className="space-y-2">
              <Label htmlFor="telefono" className="text-gray-700 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Teléfono
              </Label>
              <Input
                id="telefono"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                placeholder="+56 9 1234 5678"
                className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                data-testid="profile-telefono-input"
              />
            </div>

            {/* Email (solo lectura) */}
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input
                value={profile?.email || ''}
                disabled
                className="bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-400">El email no puede ser modificado</p>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-end pt-4 border-t border-gray-100">
              <Button 
                type="submit" 
                className="bg-[#7FD02E] hover:bg-[#6BC024] text-white"
                disabled={saving}
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar Cambios
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Telegram Notifications Card */}
      {telegram.configured && (profile?.role === 'jefatura' || profile?.role === 'solucionador' || profile?.role === 'admin') && (
        <Card className="border-gray-200 bg-white shadow-sm" data-testid="telegram-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2 text-gray-800">
              <Send className="w-5 h-5 text-[#229ED9]" />
              Notificaciones por Telegram
            </CardTitle>
            <CardDescription>
              Recibe avisos automáticos: ticket creado · 3 días antes del vencimiento · día del vencimiento.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {telegram.linked ? (
              <div className="flex items-center justify-between flex-wrap gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="w-5 h-5" />
                  <div>
                    <p className="font-semibold">Telegram conectado</p>
                    <p className="text-xs text-emerald-600/80">chat_id: <code>{telegram.chat_id}</code></p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={disconnectTelegram}
                  disabled={tgLoading}
                  className="border-rose-200 text-rose-700 hover:bg-rose-50"
                  data-testid="telegram-disconnect-btn"
                >
                  {tgLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Unplug className="w-4 h-4 mr-2" />}
                  Desvincular
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                  <li>Presiona <b>Conectar Telegram</b> abajo. Se abrirá el bot <code>@{telegram.bot_username}</code>.</li>
                  <li>Toca <b>Iniciar</b> (o envía <code>/start</code>).</li>
                  <li>Vuelve aquí y haz click en <b>Verificar estado</b>.</li>
                </ol>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={connectTelegram}
                    className="bg-[#229ED9] hover:bg-[#1B8BBE] text-white"
                    data-testid="telegram-connect-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Conectar Telegram
                  </Button>
                  <Button
                    variant="outline"
                    onClick={refreshTelegramStatus}
                    disabled={tgLoading}
                    className="border-gray-300"
                    data-testid="telegram-refresh-btn"
                  >
                    {tgLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Verificar estado
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
